import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Copy, Check, ExternalLink, DollarSign, Clock, Coins, RefreshCw, ArrowRight, Send, Edit, Save } from 'lucide-react';
import { TradingWallet, renameTradingWallet } from '../../services/tradingWalletService';
import { getBalance } from '../../services/shyftService';
import { useWalletBalances } from '../../services/walletBalanceService';
import { getWalletTokensWithMetadata } from '../../services/solanatrackerService';
import InstantWalletTransfer from './InstantWalletTransfer';

// Constants
const SOL_MINT = 'So11111111111111111111111111111111111111112';
const LAMPORTS_PER_SOL = 1000000000; // 1 SOL = 10^9 lamports

interface TokenBalanceItem {
  mint: string;
  balance: string;
  symbol?: string;
  icon?: string;
  name?: string;
  image?: string | null;
  value?: number;
  decimals?: number;
  error?: string; // Add error property for error handling
  isSOL?: boolean; // Add flag to identify SOL
}

// Extend the TradingWallet interface to include last_updated
interface ExtendedTradingWallet extends TradingWallet {
  last_updated?: string; // Optional timestamp used for balance update tracking
}

interface TradingWalletCardProps {
  wallet: ExtendedTradingWallet;
  onDelete: (publicKey: string) => void;
  onUpdate?: (updatedWallet: ExtendedTradingWallet) => void;
  className?: string;
  allWallets?: ExtendedTradingWallet[];
  userWalletAddress?: string;
  isSelected?: boolean; // New prop to indicate if this wallet is selected
  isActive?: boolean; // New prop to indicate if this wallet is the active trading wallet
  onTransferClick?: (walletPublicKey: string) => void; // New prop for transfer button click handler
  onManageClick?: (walletPublicKey: string) => void; // New prop for manage button click handler
}

// Update the CSS animation for the active-wallet-tag
const activeWalletIndicatorStyles = `
  /* Gold tag at bottom middle with animations */
  .active-wallet-tag {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%) translateY(100%);
    background: linear-gradient(135deg, #ffd700, #c19411);
    color: #111;
    font-weight: bold;
    font-size: 8px;
    padding: 2px 12px 2px;
    text-transform: uppercase;
    z-index: 20;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    box-shadow: 0 -4px 10px rgba(255, 215, 0, 0.4);
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-top: 1px solid rgba(255, 215, 0, 0.8);
    border-left: 1px solid rgba(255, 215, 0, 0.8);
    border-right: 1px solid rgba(255, 215, 0, 0.8);
    /* Animation for appearing/disappearing - faster for better feedback */
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    opacity: 0;
    transform-origin: bottom center;
  }
  
  /* Visible state for animation */
  .active-wallet-tag-visible {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
  
  .active-wallet-tag::before {
    content: '';
    display: inline-block;
    width: 5px;
    height: 5px;
    background: #111;
    border-radius: 50%;
    margin-right: 4px;
    border: 1px solid rgba(255, 215, 0, 0.8);
    animation: pulse-dot 1.5s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite;
  }
  
  /* Active wallet glow effect for the bottom of the card */
  .active-wallet-card {
    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3) !important;
    border-bottom-color: #c19411 !important;
    transition: box-shadow 0.5s ease-out, border-color 0.5s ease-out;
  }
  
  /* Card glow animation classes */
  .active-wallet-card-active {
    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3) !important;
    border-bottom-color: #c19411 !important;
    transition: box-shadow 0.5s ease-out, border-color 0.5s ease-out;
  }
  
  .active-wallet-card-inactive {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
    border-bottom-color: rgba(128, 128, 128, 0.4) !important;
    transition: box-shadow 0.5s ease-out, border-color 0.5s ease-out;
  }
  
  /* Bottom glow line with animation */
  .active-wallet-bottom-line {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(to right, transparent, #c19411, transparent);
    box-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
    z-index: 10;
    transition: opacity 0.3s ease-out, transform 0.3s ease-out;
    transform: scaleX(0);
    opacity: 0;
  }
  
  /* Visible state for bottom line */
  .active-wallet-bottom-line-visible {
    opacity: 1;
    transform: scaleX(1);
  }
`;

const TradingWalletCard: React.FC<TradingWalletCardProps> = ({ 
  wallet, 
  onDelete, 
  onUpdate,
  className = '', 
  allWallets = [],
  userWalletAddress = '',
  isSelected = false, // Default to false
  isActive = false, // Default to false
  onTransferClick,
  onManageClick
}) => {
  const [copied, setCopied] = useState(false);
  const [solBalance, setSolBalance] = useState<number | undefined>(
    typeof wallet.balance === 'number' ? wallet.balance : 0 // Default to 0 instead of undefined
  );
  const [tokenBalances, setTokenBalances] = useState<TokenBalanceItem[]>([]);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);
  const [showAllTokens, setShowAllTokens] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [walletName, setWalletName] = useState(wallet.name);
  const [lastTokenFetchTime, setLastTokenFetchTime] = useState<number>(0);
  const TOKEN_FETCH_THROTTLE = 10000; // Only fetch token balances every 10 seconds
  
  // Animation states - initialize with isActive prop
  const [tagVisible, setTagVisible] = useState(false); // Always start with invisible tag
  const [lineVisible, setLineVisible] = useState(false); // Always start with invisible line
  const [wasActive, setWasActive] = useState(isActive);
  const [animationKey, setAnimationKey] = useState(0);
  
  // Get wallet balance utility functions
  const { refreshBalances, fetchTokenBalances, getTokenInfo } = useWalletBalances();
  
  // Track the wallet update timestamp from parent
  const [walletLastUpdateFromProps, setWalletLastUpdateFromProps] = useState<number | null>(
    wallet.last_updated ? new Date(wallet.last_updated).getTime() : null
  );
  
  // Add a static class to track recent fetches globally across all wallet cards
  // This is crucial to prevent duplicate fetches when multiple events fire at once
  if (!window.ekkoTokenFetchTracker) {
    window.ekkoTokenFetchTracker = {
      recentFetches: new Map<string, number>(),
      isRecentlyFetched: function(walletAddress: string): boolean {
        const lastFetch = this.recentFetches.get(walletAddress);
        return !!lastFetch && (Date.now() - lastFetch < 10000); // 10 second global throttle
      },
      recordFetch: function(walletAddress: string): void {
        this.recentFetches.set(walletAddress, Date.now());
      }
    };
  }
  
  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };
  
  const copyAddress = () => {
    navigator.clipboard.writeText(wallet.public_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Start editing wallet name
  const startEditing = () => {
    setIsEditing(true);
  };
  
  // Save wallet name
  const saveWalletName = async () => {
    if (walletName.trim() !== wallet.name) {
      try {
        await renameTradingWallet(userWalletAddress, wallet.public_key, walletName.trim());
        setIsEditing(false);
        if (onUpdate) {
          const updatedWallet = { ...wallet, name: walletName.trim() };
          onUpdate(updatedWallet);
        }
      } catch (error) {
        console.error("Error renaming wallet:", error);
      }
    } else {
      setIsEditing(false);
    }
  };
  
  // Handle wallet name change
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWalletName(e.target.value);
  };
  
  // Handle key press in wallet name input
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      saveWalletName();
    } else if (e.key === 'Escape') {
      setWalletName(wallet.name);
      setIsEditing(false);
    }
  };

  // Safely format the balance with a null/undefined check and keep a consistent format
  const formatBalance = (balance: number | undefined) => {
    // Always show a value in the same format
    if (balance === undefined || balance === null) {
      return '0.0000'; // Removed "SOL" label
    }
    
    // Format with exactly 4 decimal places for consistency, removed "SOL" label
    return `${balance.toFixed(4)}`;
  };

  // Update the formatTokenBalance function to avoid scientific notation for small values
  const formatTokenBalance = (balance: string) => {
    const num = parseFloat(balance);
    if (isNaN(num)) return '0.0000';
    
    // Always use fixed decimal notation, never scientific
    if (num < 0.0001) {
      return num.toFixed(8); // Very small numbers get 8 decimals
    } else if (num < 0.01) {
      return num.toFixed(6); // Small numbers get 6 decimals
    } else if (num < 1) {
      return num.toFixed(4); // Medium numbers get 4 decimals
    } else if (num < 10000) {
      return num.toFixed(4); // Larger numbers get 4 decimals
    } else {
      return num.toLocaleString(undefined, { maximumFractionDigits: 2 }); // Huge numbers
    }
  };

  // Format creation date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  // On initial mount, set the isActive state correctly
  useEffect(() => {
    if (isActive) {
      // Use a small timeout to let the component render first
      // This creates a visible animation on mount
      setTimeout(() => {
        setLineVisible(true);
        setTimeout(() => {
          setTagVisible(true);
        }, 100);
      }, 10);
    }
  }, []);
  
  // Improved active state animations
  useEffect(() => {
    if (isActive) {
      // Wallet became active - animate in stages for smoother effect
      setAnimationKey(prev => prev + 1);
      setWasActive(true);
      
      // Reset states to ensure animation runs properly
      setTagVisible(false);
      setLineVisible(false);
      
      // Use RAF to ensure UI has updated before starting animation
      requestAnimationFrame(() => {
        // First animate the bottom line
        requestAnimationFrame(() => {
          setLineVisible(true);
          
          // Then after a short delay, show the Active label
          setTimeout(() => {
            setTagVisible(true);
          }, 100);
        });
      });
    } else {
      // Wallet became inactive - animate in reverse order
      // First hide the Active label
      setTagVisible(false);
      
      // After a delay, hide the bottom line
      setTimeout(() => {
        setLineVisible(false);
      }, 150);
      
      // After animation completes, clean up
      const timer = setTimeout(() => {
        setWasActive(false);
      }, 500); // Match the CSS transition time
      
      return () => clearTimeout(timer);
    }
  }, [isActive, wallet.public_key]);
  
  // Improved event handler for active wallet changes
  useEffect(() => {
    const handleActiveWalletChange = (event: CustomEvent<{
      previousWallet: string | null,
      currentWallet: string | null,
      timestamp: number
    }>) => {
      const { previousWallet, currentWallet } = event.detail;
      
      // If this wallet became active
      if (currentWallet === wallet.public_key) {
        setAnimationKey(prev => prev + 1);
        setWasActive(true);
        
        // Reset states to ensure animation runs properly
        setTagVisible(false);
        setLineVisible(false);
        
        // Use RAF to ensure UI has updated before starting animation
        requestAnimationFrame(() => {
          setLineVisible(true);
          setTimeout(() => {
            setTagVisible(true);
          }, 100);
        });
      } 
      // If this wallet was previously active but is no longer active
      else if (previousWallet === wallet.public_key) {
        setTagVisible(false);
        
        setTimeout(() => {
          setLineVisible(false);
          
          // After animation completes, clean up
          setTimeout(() => {
            setWasActive(false);
          }, 300);
        }, 150);
      }
    };
    
    // Add event listener
    window.addEventListener('ekko:active_wallet_changed', handleActiveWalletChange as EventListener);
    
    // Clean up
    return () => {
      window.removeEventListener('ekko:active_wallet_changed', handleActiveWalletChange as EventListener);
    };
  }, [wallet.public_key]);
  
  // Update local state when wallet props change, but only if the new props are more recent
  useEffect(() => {
    const newWalletTimestamp = wallet.last_updated ? new Date(wallet.last_updated).getTime() : null;
    
    // If we have a new timestamp and it's newer than our current one, or we don't have one yet
    if (newWalletTimestamp && (!walletLastUpdateFromProps || newWalletTimestamp > walletLastUpdateFromProps)) {
      // Update our timestamp tracker
      setWalletLastUpdateFromProps(newWalletTimestamp);
      
      // Also update last updated to match or be slightly after the wallet update
      // This prevents gRPC updates that came in before this prop update from overriding it
      if (!lastUpdated || newWalletTimestamp > lastUpdated) {
        setLastUpdated(newWalletTimestamp);
      }
    }
  }, [wallet, walletLastUpdateFromProps]);
  
  // Handle SOL balance update from ekko:sol_balance_update events
  useEffect(() => {
    const handleSolBalanceEvent = (event: CustomEvent<{address: string, balance: number, error?: boolean}>) => {
      const { address, balance, error } = event.detail;
      
      // Only process updates for this specific trading wallet
      if (address === wallet.public_key) {
        // Convert from lamports to SOL
        const newSolBalance = balance / LAMPORTS_PER_SOL;
        
        // Only update if we received a valid balance (greater than or equal to 0)
        // This prevents wiping out existing balances with invalid ones
        if (newSolBalance >= 0) {
          setSolBalance(newSolBalance);
          
          // Stop loading state
          setIsLoadingBalances(false);
          
          // Update last updated timestamp
          setLastUpdated(Date.now());
        }
      }
    };

    // Also handle failed token balance updates
    const handleTokenBalanceFailedEvent = (event: CustomEvent<{address: string, error: boolean}>) => {
      const { address } = event.detail;
      
      // Only process for this specific wallet
      if (address === wallet.public_key) {
        // Stop loading state even if there was an error
        setIsLoadingBalances(false);
      }
    };

    // Add event listeners
    window.addEventListener('ekko:sol_balance_update', handleSolBalanceEvent as EventListener);
    window.addEventListener('ekko:token_balances_failed', handleTokenBalanceFailedEvent as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('ekko:sol_balance_update', handleSolBalanceEvent as EventListener);
      window.removeEventListener('ekko:token_balances_failed', handleTokenBalanceFailedEvent as EventListener);
    };
  }, [wallet.public_key]);
  
  // Perform initial balance fetch on mount
  useEffect(() => {
    // Perform initial balance check immediately on mount
    const fetchInitialBalance = async () => {
      try {
        
        // Only show loading if we don't already have a balance
        if (solBalance === undefined) {
          setIsLoadingBalances(true);
        }
        
        const lamports = await getBalance(wallet.public_key);
        
        // Always update if we get a valid balance (>=0)
        if (lamports >= 0) {
          const solBalanceInSol = lamports / LAMPORTS_PER_SOL;

          
          // Update the balance in state directly
          setSolBalance(solBalanceInSol);
          setIsLoadingBalances(false);
          setLastUpdated(Date.now());
        } else {
          setIsLoadingBalances(false);
        }
      } catch (error) {
        // Don't clear existing balance on error
        setIsLoadingBalances(false);
      }
    };
    
    // Always fetch on mount to ensure we have fresh data
    fetchInitialBalance();
    
    return () => {
      // No cleanup required
    };
  }, [wallet.public_key]); // Remove solBalance from dependency array
  
  // Update the processTokenForDisplay function to use the logo from the response
  const processTokenForDisplay = (tokenData: any, isSol = false): TokenBalanceItem => {
    // Handle SOL special case
    if (isSol || tokenData.mint === SOL_MINT) {
      const balance = tokenData.balance || '0';
      const isLamports = parseFloat(balance) > 1000000000;
      const formattedBalance = isLamports ? 
        (parseFloat(balance) / LAMPORTS_PER_SOL).toString() : 
        balance;
      
      return {
        mint: tokenData.mint,
        balance: formattedBalance,
        symbol: "SOL",
        name: "Solana",
        // Use the image from the response if available, otherwise use a fallback
        image: tokenData.image || "https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png",
        value: tokenData.value || 0,
        decimals: 9,
        isSOL: true
      };
    }
    
    // Regular token
    return {
      mint: tokenData.mint,
      balance: tokenData.balance || '0',
      symbol: tokenData.symbol || tokenData.mint.slice(0, 4),
      name: tokenData.name || 'Unknown Token',
      image: tokenData.image || null,
      value: tokenData.value || 0,
      decimals: 9, // Default to 9 decimals
      isSOL: false
    };
  };
      
  // Update the fetchTokenBalancesWithMetadata function with SOL handling
  const fetchTokenBalancesWithMetadata = async (retryCount = 1, forceRefresh = false) => {
    try {
      // First check global fetch tracker to avoid duplicate fetches across card instances
      if (!forceRefresh && window.ekkoTokenFetchTracker.isRecentlyFetched(wallet.public_key)) {
        console.log(`GLOBAL THROTTLE: Skipping token balance fetch for wallet: ${wallet.public_key}`);
        return;
      }

      // Apply throttling to prevent excessive calls
      const now = Date.now();
      if (!forceRefresh && now - lastTokenFetchTime < TOKEN_FETCH_THROTTLE) {
        console.log(`LOCAL THROTTLE: Skipping token balance fetch for wallet: ${wallet.public_key}`);
        return;
      }
      
      // Record this fetch in global tracker and update local timestamp
      window.ekkoTokenFetchTracker.recordFetch(wallet.public_key);
      setLastTokenFetchTime(now);
        
      // Start loading state if not already loading
      if (!isLoadingBalances) {
        setIsLoadingBalances(true);
      }
      
      console.log(`Fetching token balances for wallet: ${wallet.public_key}`);
      
      // Get token balances from Solana Tracker API with metadata
      const response = await getWalletTokensWithMetadata(wallet.public_key);
      
      if (response.status === 'success') {
        if (response.data.tokens && response.data.tokens.length > 0) {
          console.log(`Found ${response.data.tokens.length} tokens for wallet ${wallet.public_key}`);
          
          // Format tokens for display
          const tokens = response.data.tokens.map((token: any) => 
            processTokenForDisplay(token, token.mint === SOL_MINT)
          );
          
          // Sort tokens - SOL first, then tokens with value, then by balance
          const sortedTokens = tokens.sort((a: TokenBalanceItem, b: TokenBalanceItem) => {
            // SOL always comes first
            if (a.isSOL) return -1;
            if (b.isSOL) return 1;
            
            // Then sort by value or balance
            if ((a.value || 0) > 0 && (b.value || 0) === 0) return -1;
            if ((a.value || 0) === 0 && (b.value || 0) > 0) return 1;
            
            if ((a.value || 0) > 0 && (b.value || 0) > 0) {
              return (b.value || 0) - (a.value || 0);
            } else {
              return parseFloat(b.balance) - parseFloat(a.balance);
            }
          });
          
          // Update token balances
          setTokenBalances(sortedTokens);
        } else {
          console.log(`No tokens found for wallet ${wallet.public_key} via Solana Tracker, falling back to Shyft`);
          
          // Fall back to Shyft service for token balances
          try {
            // Use getTokenBalances from the walletBalances service
            const shyftTokens = await fetchTokenBalances(wallet.public_key);
            
            // Process token balances to match our format
            const tokenPromises = Object.entries(shyftTokens)
              .filter(([_, balance]) => parseFloat(balance) > 0) // Only non-zero balances
              .map(async ([mint, balance]) => {
                // For non-SOL tokens, get metadata first
                if (mint !== SOL_MINT) {
                  const tokenInfo = await getTokenInfo(mint);
                  
                  // Process with our helper function
                  return processTokenForDisplay({
              mint,
              balance,
                    symbol: tokenInfo?.symbol,
                    name: tokenInfo?.name,
                    image: tokenInfo?.image_uri
                  });
                }
                
                // For SOL, use our helper with isSol=true
                return processTokenForDisplay({
                  mint,
                  balance
                }, true);
              });
              
            const tokensFromShyft = await Promise.all(tokenPromises);
            
            if (tokensFromShyft.length > 0) {
              console.log(`Found ${tokensFromShyft.length} tokens via Shyft for wallet ${wallet.public_key}`);
              
              // Sort tokens - SOL first, then by balance
              const sortedTokens = tokensFromShyft.sort((a: TokenBalanceItem, b: TokenBalanceItem) => {
                // SOL always comes first
                if (a.isSOL) return -1;
                if (b.isSOL) return 1;
                
                // Then sort by balance
                return parseFloat(b.balance) - parseFloat(a.balance);
              });
              
              setTokenBalances(sortedTokens);
            } else {
              // Clear token balances if none found
              setTokenBalances([]);
            }
          } catch (shyftError) {
            console.error('Shyft fallback also failed:', shyftError);
            // Clear token balances to ensure we don't display stale data
            setTokenBalances([]);
          }
        }
      } else if (response.error) {
        console.error(`Error response from API: ${response.error}`);
        
        // Try to retry if we have retries left
        if (retryCount > 0) {
          console.log(`Retrying token balance fetch, attempts left: ${retryCount}`);
          setTimeout(() => {
            fetchTokenBalancesWithMetadata(retryCount - 1);
          }, 2000); // Wait 2 seconds before retry
      return;
    }
    
        // Fall back to Shyft service for token balances if SolanaTracker failed
        try {
          // Use getTokenBalances from the walletBalances service
          const shyftTokens = await fetchTokenBalances(wallet.public_key);
          
          // Process token balances to match our format
          const tokenPromises = Object.entries(shyftTokens)
            .filter(([_, balance]) => parseFloat(balance) > 0) // Only non-zero balances
            .map(async ([mint, balance]) => {
              // For non-SOL tokens, get metadata first
              if (mint !== SOL_MINT) {
                const tokenInfo = await getTokenInfo(mint);
                
                // Process with our helper function
                return processTokenForDisplay({
                  mint,
                  balance,
                  symbol: tokenInfo?.symbol,
                  name: tokenInfo?.name,
                  image: tokenInfo?.image_uri
                });
              }
              
              // For SOL, use our helper with isSol=true
              return processTokenForDisplay({
                mint,
                balance
              }, true);
            });
            
          const tokensFromShyft = await Promise.all(tokenPromises);
          
          if (tokensFromShyft.length > 0) {
            console.log(`Found ${tokensFromShyft.length} tokens via Shyft for wallet ${wallet.public_key}`);
            setTokenBalances(tokensFromShyft);
          } else {
            // No more retries, show error only if we don't have any tokens
            if (tokenBalances.length === 0) {
              // Only set error token if we don't have any tokens yet
              setTokenBalances([{
                mint: 'error',
                balance: '0',
                symbol: 'ERROR',
                name: 'Failed to load tokens',
                error: response.error
              }]);
            }
          }
        } catch (shyftError) {
          console.error('Shyft fallback also failed:', shyftError);
          // No more retries, show error
          if (tokenBalances.length === 0) {
            // Only set error token if we don't have any tokens yet
            setTokenBalances([{
              mint: 'error',
              balance: '0',
              symbol: 'ERROR',
              name: 'Failed to load tokens',
              error: response.error
            }]);
          }
        }
      }
      
      // Update last updated timestamp and stop loading
      setLastUpdated(Date.now());
      setIsLoadingBalances(false);
    } catch (error) {
      console.error('Error fetching token balances with metadata:', error);
      
      // Try to retry if we have retries left
      if (retryCount > 0) {
        console.log(`Retrying token balance fetch after error, attempts left: ${retryCount}`);
        setTimeout(() => {
          fetchTokenBalancesWithMetadata(retryCount - 1);
        }, 2000); // Wait 2 seconds before retry
        return;
      }
      
      // Fall back to Shyft service if all retries failed
      try {
        // Use getTokenBalances from the walletBalances service
        const shyftTokens = await fetchTokenBalances(wallet.public_key);
      
        // Process token balances to match our format
        const tokenPromises = Object.entries(shyftTokens)
          .filter(([_, balance]) => parseFloat(balance) > 0) // Only non-zero balances
          .map(async ([mint, balance]) => {
            // For non-SOL tokens, get metadata first
            if (mint !== SOL_MINT) {
            const tokenInfo = await getTokenInfo(mint);
            
              // Process with our helper function
              return processTokenForDisplay({
              mint,
              balance,
                symbol: tokenInfo?.symbol,
                name: tokenInfo?.name,
                image: tokenInfo?.image_uri
              });
            }
            
            // For SOL, use our helper with isSol=true
            return processTokenForDisplay({
              mint,
              balance
            }, true);
          });
          
        const tokensFromShyft = await Promise.all(tokenPromises);
        
        if (tokensFromShyft.length > 0) {
          console.log(`Found ${tokensFromShyft.length} tokens via Shyft for wallet ${wallet.public_key}`);
          setTokenBalances(tokensFromShyft);
        } else {
          // Handle errors gracefully after all retries
          if (tokenBalances.length === 0) {
            // Only set error token if we don't have any tokens yet
            setTokenBalances([{
              mint: 'error',
              balance: '0',
              symbol: 'ERROR',
              name: 'Failed to load tokens',
              error: error instanceof Error ? error.message : 'Unknown error'
            }]);
          }
      }
      } catch (shyftError) {
        console.error('Shyft fallback also failed:', shyftError);
        // Handle errors gracefully after all retries
        if (tokenBalances.length === 0) {
          // Only set error token if we don't have any tokens yet
          setTokenBalances([{
            mint: 'error',
            balance: '0',
            symbol: 'ERROR',
            name: 'Failed to load tokens',
            error: error instanceof Error ? error.message : 'Unknown error'
          }]);
        }
      }
      
      setIsLoadingBalances(false);
    }
  };

  // Update the effect that sets up periodic token balance fetching
  useEffect(() => {
    // Only attempt to fetch token balances if we have a valid wallet public key
    if (!wallet || !wallet.public_key || wallet.public_key.trim() === '') {
      console.warn('Invalid wallet public key - skipping token balance fetch');
      return;
    }
    
    // Define a function to check if the public key is valid
    const isValidPublicKey = (key: string): boolean => {
      // Basic check: public key should be a string with length of 32-44 characters
      return typeof key === 'string' && key.trim().length >= 32 && key.trim().length <= 44;
    };

    // Double-check that we have a valid wallet public key
    if (!isValidPublicKey(wallet.public_key)) {
      console.error(`Invalid wallet public key format: ${wallet.public_key}`);
      return;
    }

    // Fetch token balances on initial mount with valid wallet
    console.log(`Setting up token balance fetch for wallet: ${wallet.public_key}`);
    // Force the initial fetch
    fetchTokenBalancesWithMetadata(1, true);
    
    // Set up interval to refresh token balances less frequently
    const intervalId = setInterval(() => {
      if (isValidPublicKey(wallet.public_key)) {
        // Use normal throttled fetch for periodic updates
        fetchTokenBalancesWithMetadata();
      } else {
        console.warn(`Skipping periodic fetch for invalid wallet: ${wallet.public_key}`);
      }
    }, 30000); // Changed from 10000 to 30000 (30 seconds)
    
    return () => clearInterval(intervalId);
  }, [wallet.public_key]);
  
  // Update the handle refresh function to force a refresh
  const handleRefresh = () => {
    setIsLoadingBalances(true);
    // Refresh SOL balance via existing mechanism
    refreshBalances(wallet.public_key);
    // Force refresh token balances with metadata
    fetchTokenBalancesWithMetadata(1, true);
  };
  
  // Determine which tokens to display
  const displayTokens = showAllTokens 
    ? tokenBalances 
    : tokenBalances.slice(0, 3);
  
  // Handle transfer button click
  const handleTransferClick = useCallback(() => {
    if (onTransferClick) {
      onTransferClick(wallet.public_key);
    } else {
      setInitialTransferTab('trading');
      setShowTransferModal(true);
    }
  }, [onTransferClick, wallet.public_key]);

  // Handle manage button click
  const handleManageClick = useCallback(() => {
    if (onManageClick) {
      onManageClick(wallet.public_key);
    } else {
      setInitialTransferTab('manage');
      setShowTransferModal(true);
    }
  }, [onManageClick, wallet.public_key]);

  // State to track which tab to show in the transfer modal
  const [initialTransferTab, setInitialTransferTab] = useState<'main' | 'trading' | 'manage'>('trading');

  // Handle transfer success - remove manual balance fetching entirely
  const handleTransferSuccess = useCallback(() => {
    setShowTransferModal(false);
    // DO NOT manually fetch balances - rely solely on gRPC updates
    // The balances will update via websocket events automatically
  }, []);
  
  // Make render depend on animation key to force refresh
  useEffect(() => {
    // Remove excessive logging
    // console.log(`[TradingWalletCard] Animation key changed: ${animationKey} for wallet ${wallet.public_key}`);
  }, [animationKey, wallet.public_key]);
  
  // Debug log for renders
  // console.log(`[TradingWalletCard] Rendering wallet ${wallet.public_key} - isActive: ${isActive}, wasActive: ${wasActive}, tagVisible: ${tagVisible}, lineVisible: ${lineVisible}`);
  
  // Add the TokenBalancesSection component
  const TokenBalancesSection = () => {
    // Add state to track which token's address has been copied
    const [copiedTokenMint, setCopiedTokenMint] = useState<string | null>(null);
    
    // Function to copy token mint address to clipboard
    const copyTokenAddress = (e: React.MouseEvent, mint: string) => {
      // Stop event propagation to prevent selecting the token card
      e.stopPropagation();
      e.preventDefault();
      
      // Copy to clipboard
      navigator.clipboard.writeText(mint);
      setCopiedTokenMint(mint);
      setTimeout(() => setCopiedTokenMint(null), 2000); // Reset after 2 seconds
    };

    // Check for error state
    const hasError = tokenBalances.length === 1 && tokenBalances[0].mint === 'error';
    
    // If we have an error, show it
    if (hasError) {
      return (
        <div className="text-xs text-red-400 text-center py-2">
          {tokenBalances[0].error || 'Failed to load token balances'}
        </div>
      );
    }
    
    // If we have no tokens, show a clear message
    if (tokenBalances.length === 0) {
      return (
        <div className="text-xs text-gray-500 text-center py-2">
          No token balances found
        </div>
      );
    }
    
    // Determine which tokens to display
    const displayTokens = showAllTokens ? tokenBalances : tokenBalances.slice(0, 3);
    
    return (
      <div className="divide-y divide-gray-800/10">
        {displayTokens.map((token) => (
          <div key={token.mint} className="flex items-center justify-between py-1.5">
            <div className="flex items-center space-x-2">
              {token.image ? (
                <img 
                  src={token.image} 
                  alt={token.symbol || 'Token'} 
                  className="w-5 h-5 rounded-full object-cover bg-gray-800"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    // Default fallback using first letter of symbol
                    target.onerror = null; // Prevent infinite loop
                    target.src = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="%23ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" fill="%23333"/><text x="50%" y="55%" font-family="Arial" font-size="12" fill="white" text-anchor="middle" dominant-baseline="middle">${token.symbol?.charAt(0) || '?'}</text></svg>`;
                  }}
                />
              ) : (
                <div className="w-5 h-5 rounded-full bg-gray-700 flex items-center justify-center text-xs text-white">
                  {token.symbol?.charAt(0) || '?'}
                </div>
              )}
              <div className="flex items-center">
                <div>
                  <div className="text-white text-xs font-medium group flex items-center">
                    {token.symbol || 'Unknown'}
                    <button
                      onClick={(e) => copyTokenAddress(e, token.mint)}
                      className="ml-1.5 opacity-60 hover:opacity-100 text-gray-400 hover:text-yellow-500 transition-colors duration-200"
                      title={`Copy ${token.symbol || 'token'} address: ${token.mint}`}
                    >
                      {copiedTokenMint === token.mint ? 
                        <Check size={10} className="text-green-400" /> : 
                        <Copy size={10} />
                      }
                    </button>
                  </div>
                  {token.name && (
                    <div className="text-gray-400 text-[10px]">{token.name.length > 14 ? `${token.name.substring(0, 14)}...` : token.name}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-white text-xs">{formatTokenBalance(token.balance)}</div>
              {token.value && token.value > 0 && (
                <div className="text-gray-400 text-[10px]">${token.value.toFixed(2)}</div>
              )}
            </div>
          </div>
        ))}
        
        {/* Show all/less tokens toggle */}
        {tokenBalances.length > 3 && (
          <button
            onClick={() => setShowAllTokens(!showAllTokens)}
            className="card-action-button text-[10px] text-yellow-500 hover:text-yellow-400 transition-colors duration-200 mt-1 w-full text-center"
          >
            {showAllTokens ? 'Show Less' : `Show All (${tokenBalances.length})`}
          </button>
        )}
      </div>
    );
  };
  
  // Update the token balance request handler to use global fetch tracking
  useEffect(() => {
    if (!wallet || !wallet.public_key) return;
    
    // Handler for token balance update requests
    const handleTokenBalanceRequest = (event: CustomEvent<{walletPublicKey: string}>) => {
      const { walletPublicKey } = event.detail;
      
      // Only process updates for this specific trading wallet
      if (walletPublicKey === wallet.public_key) {
        // Check global tracker first
        if (window.ekkoTokenFetchTracker.isRecentlyFetched(wallet.public_key)) {
          console.log(`GLOBAL THROTTLE: Skipping token balance request for wallet: ${wallet.public_key}`);
          return;
        }
        console.log(`Token balance update requested for wallet: ${wallet.public_key}`);
        // Use throttled fetch
        fetchTokenBalancesWithMetadata();
      }
    };
    
    // Handler for global wallet entry event
    const handleWalletPageEntry = () => {
      // Check global fetch tracker first - most important throttle
      if (window.ekkoTokenFetchTracker.isRecentlyFetched(wallet.public_key)) {
        console.log(`GLOBAL THROTTLE: Ignoring wallet page entry event for wallet: ${wallet.public_key}`);
        return;
      }
      
      // Then check local throttling
      const now = Date.now();
      if (now - lastTokenFetchTime > TOKEN_FETCH_THROTTLE) {
        console.log(`Wallet page entry detected - fetching token balances for wallet: ${wallet.public_key}`);
        fetchTokenBalancesWithMetadata();
      } else {
        console.log(`LOCAL THROTTLE: Skipping fetch due to recent update for wallet: ${wallet.public_key}`);
      }
    };
    
    // Add event listeners
    window.addEventListener('ekko:request_token_balances', handleTokenBalanceRequest as EventListener);
    window.addEventListener('ekko:wallet_page_entry', handleWalletPageEntry as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('ekko:request_token_balances', handleTokenBalanceRequest as EventListener);
      window.removeEventListener('ekko:wallet_page_entry', handleWalletPageEntry as EventListener);
    };
  }, [wallet.public_key, lastTokenFetchTime]); // Add lastTokenFetchTime to dependencies
  
  return (
    <>
      {/* Include active wallet indicator styles */}
      <style>{activeWalletIndicatorStyles}</style>
      
      <div className={`rounded-lg overflow-hidden backdrop-blur-sm bg-dark-grey/60 border border-gray-700/40 shadow-xl hover:shadow-2xl transition-all duration-300 ${className} ${
        isSelected ? 'outline outline-2 outline-[#47be8f] outline-offset-[3px] relative z-20' : ''
      } relative ${tagVisible ? 'active-wallet-card-active' : 'active-wallet-card-inactive'}`}>
        {/* Active wallet indicator - improved animation handling */}
        {(isActive || wasActive) && (
          <>
            <div 
              key={`active-tag-${animationKey}`} 
              className={`active-wallet-tag ${tagVisible ? 'active-wallet-tag-visible' : ''}`}
            >
              ACTIVE
            </div>
            <div 
              key={`active-line-${animationKey}`}
              className={`active-wallet-bottom-line ${lineVisible ? 'active-wallet-bottom-line-visible' : ''}`}
            ></div>
          </>
        )}
        
        {/* Card header */}
        <div className="p-3 border-b border-gray-700/30 bg-gradient-to-r from-gray-800/30 to-gray-900/20">
          <div className="flex justify-between items-center mb-2">
            {isEditing ? (
              <div className="flex items-center space-x-1">
                <input
                  type="text"
                  value={walletName}
                  onChange={handleNameChange}
                  onKeyDown={handleKeyPress}
                  onBlur={saveWalletName}
                  autoFocus
                  className="bg-gray-800/50 border border-gray-600/50 text-sm text-white/90 px-2 py-0.5 rounded focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500 w-36"
                />
                <button
                  onClick={saveWalletName}
                  className="text-green-500 hover:text-green-400 transition-colors duration-200"
                >
                  <Save size={14} />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-1">
                <h3 className="text-sm font-medium text-white/90">{walletName}</h3>
                <button
                  onClick={startEditing}
                  className="card-action-button text-gray-500 hover:text-yellow-500 transition-colors duration-200 ml-1"
                  title="Edit Wallet Name"
                >
                  <Edit size={12} />
                </button>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <button 
                type="button" 
                className="card-action-button text-yellow-500 hover:text-yellow-400 transition-colors duration-200"
                title="Transfer funds"
                onClick={() => handleTransferClick()}
              >
                <Send size={14} />
              </button>
            </div>
          </div>
          
          <div className="flex items-center">
            <span className="text-xs text-gray-400/90 select-none mr-1">Address:</span>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-300">{formatAddress(wallet.public_key)}</span>
          <button
                onClick={copyAddress} 
                className="card-action-button text-gray-500 hover:text-yellow-500 transition-colors duration-200"
                title="Copy Address"
              >
                {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
          </button>
              <a
                href={`https://solscan.io/account/${wallet.public_key}`}
                target="_blank"
                rel="noopener noreferrer"
                className="card-action-button text-gray-500 hover:text-yellow-500 transition-colors duration-200"
                title="View on SolScan"
              >
                <ExternalLink size={12} />
              </a>
            </div>
          </div>
          
          <div className="flex items-center mt-1">
            <Clock size={10} className="text-gray-500/80 mr-1" />
            <span className="text-[10px] text-gray-500/80">
              Created {formatDate(wallet.created_at)}
            </span>
          </div>
        </div>
        
        {/* Balance section */}
        <div className="p-3 border-b border-gray-700/30 bg-gray-800/10">
          <div className="mb-1 text-xs text-gray-400/90 font-medium">
            <div className="flex justify-between items-center">
              <span>Balances</span>
            <button
                onClick={handleRefresh}
                disabled={isLoadingBalances}
                className={`card-action-button text-gray-400 hover:text-yellow-500 transition-colors duration-200 ${isLoadingBalances ? 'animate-spin' : ''}`}
                title="Refresh Balances"
              >
                <RefreshCw size={12} />
            </button>
            </div>
          </div>
          
          {/* Enhanced Token balances with metadata */}
          <TokenBalancesSection />
          
          {/* Loading indicator - only show when no tokens and loading */}
          {isLoadingBalances && tokenBalances.length === 0 && (
            <div className="text-xs text-gray-500 text-center py-1 flex items-center justify-center gap-1">
              <RefreshCw size={10} className="animate-spin" />
              Loading balances...
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-2 bg-gradient-to-b from-gray-700/10 to-gray-800/20 flex justify-between">
          <div>
            <button 
              type="button"
              onClick={handleManageClick}
              className="card-action-button glass-button py-1 px-2 text-[10px] flex items-center gap-1 bg-gray-700/30 hover:bg-gray-700/50 text-white/90 rounded border border-gray-600/20 transition-all duration-200"
            >
              <Edit size={10} />
              Manage
            </button>
          </div>
          <div>
            <button 
              type="button"
              onClick={() => handleTransferClick()}
              className="card-action-button glass-button py-1 px-2 text-[10px] flex items-center gap-1 bg-gray-700/30 hover:bg-gray-700/50 text-white/90 rounded border border-gray-600/20 transition-all duration-200"
            >
              <Send size={10} />
              Transfer
            </button>
          </div>
        </div>
      </div>
      
      {/* Transfer Modal - only show locally if parent doesn't handle it */}
      {showTransferModal && (
        <InstantWalletTransfer
          wallets={allWallets}
          userWalletAddress={userWalletAddress}
          sourceWalletId={wallet.public_key}
          onClose={() => setShowTransferModal(false)}
          onSuccess={handleTransferSuccess}
          initialTab={initialTransferTab}
        />
      )}
    </>
  );
};

// Create optimized comparison function for React.memo to prevent unnecessary re-renders
const arePropsEqual = (prevProps: TradingWalletCardProps, nextProps: TradingWalletCardProps) => {
  // Only re-render when these important props change
  return (
    prevProps.wallet.public_key === nextProps.wallet.public_key &&
    prevProps.wallet.name === nextProps.wallet.name &&
    prevProps.wallet.balance === nextProps.wallet.balance &&
    prevProps.wallet.last_updated === nextProps.wallet.last_updated &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isActive === nextProps.isActive
  );
};

// Export with React.memo to prevent unnecessary re-renders
export default React.memo(TradingWalletCard, arePropsEqual);

// Augment the Window interface to include our global tracker
declare global {
  interface Window {
    ekkoTokenFetchTracker: {
      recentFetches: Map<string, number>;
      isRecentlyFetched: (walletAddress: string) => boolean;
      recordFetch: (walletAddress: string) => void;
    };
  }
}